import { useMemo, useState, type ReactNode } from 'react'
import { Wand2, AlertCircle, ArrowRight, Plus, X } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { toast } from '../../lib/shadcn/sonner'
import { useBulkEditVehicles } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import {
  type FleetRow, fleetStatusLabel, LIFECYCLE_STATUS_OPTIONS, HOLD_REASON_OPTIONS, LOCATION_OPTIONS,
  holdReasonLabel, isActive, rowActiveInput,
} from '../../utils/fleet'

type FieldKey =
  | 'location' | 'status' | 'operational' | 'in_general_fleet' | 'hold_reason'
  | 'telematics_status' | 'licensing_status' | 'compliance_status'
  | 'next_service_date' | 'license_expiry' | 'repairs_due'

type FieldKind = 'select' | 'date' | 'text'

type FieldSpec = {
  key: FieldKey
  label: string
  kind: FieldKind
  patchKey: string
  options?: { value: string; label: string }[]
}

const YES_NO = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]

const FIELDS: FieldSpec[] = [
  { key: 'location', label: 'Location', kind: 'select', patchKey: 'current_location',
    options: LOCATION_OPTIONS.map((l) => ({ value: l, label: l })) },
  { key: 'status', label: 'Lifecycle status', kind: 'select', patchKey: 'status',
    options: LIFECYCLE_STATUS_OPTIONS.filter((o) => o.code !== 'written_off')
      .map((o) => ({ value: o.code, label: o.label })) },
  { key: 'operational', label: 'Operational', kind: 'select', patchKey: 'operational', options: YES_NO },
  { key: 'in_general_fleet', label: 'In general fleet', kind: 'select', patchKey: 'in_general_fleet', options: YES_NO },
  // Single-valued on purpose. A bulk edit sets one hold reason across a batch;
  // a car that carries two is a car with its own story and belongs in its own
  // dialog. 'none' clears the hold entirely.
  { key: 'hold_reason', label: 'Hold reason', kind: 'select', patchKey: 'hold_reason',
    options: [
      { value: 'none', label: 'None (clear the hold)' },
      ...HOLD_REASON_OPTIONS.map((o) => ({ value: o.code, label: o.label })),
    ] },
  { key: 'telematics_status', label: 'Telematics status', kind: 'text', patchKey: 'telematics_status' },
  { key: 'licensing_status', label: 'Licensing status', kind: 'text', patchKey: 'licensing_status' },
  { key: 'compliance_status', label: 'Compliance status', kind: 'text', patchKey: 'compliance_status' },
  { key: 'next_service_date', label: 'Next service date', kind: 'date', patchKey: 'next_service_date' },
  { key: 'license_expiry', label: 'Licence expiry', kind: 'date', patchKey: 'license_expiry' },
  { key: 'repairs_due', label: 'Repairs due', kind: 'text', patchKey: 'repairs_due' },
]

const SPEC = Object.fromEntries(FIELDS.map((f) => [f.key, f])) as Record<FieldKey, FieldSpec>

function currentValue(field: FieldKey, row: FleetRow): string {
  switch (field) {
    case 'location': return row.location ?? ''
    case 'status': return row.lifecycle_status
    case 'operational': return row.operational === true ? 'yes' : 'no'
    case 'in_general_fleet': return row.in_general_fleet === true ? 'yes' : 'no'
    case 'hold_reason': return (row.hold_reasons ?? [])[0] ?? 'none'
    case 'telematics_status': return row.telematics_status ?? ''
    case 'licensing_status': return row.licensing_status ?? ''
    case 'compliance_status': return row.compliance_status ?? ''
    case 'next_service_date': return row.next_service_date?.slice(0, 10) ?? ''
    case 'license_expiry': return row.license_expiry?.slice(0, 10) ?? ''
    case 'repairs_due': return row.repairs_due ?? ''
  }
}

function labelFor(field: FieldKey, value: string): string {
  if (value === '') return 'Not set'
  switch (field) {
    case 'status': return fleetStatusLabel(value)
    case 'operational':
    case 'in_general_fleet': return value === 'yes' ? 'Yes' : 'No'
    case 'hold_reason':
      return value === 'none' ? 'None' : holdReasonLabel(value)
    default: return value
  }
}

function patchValue(field: FieldKey, value: string): unknown {
  switch (field) {
    case 'operational':
    case 'in_general_fleet': return value === 'yes'
    case 'hold_reason': return value === 'none' ? null : value
    default: return value === '' ? null : value
  }
}

type Edit = { field: FieldKey; value: string }

/** The four conditions as this batch would leave them on one row. */
function pendingActiveInput(row: FleetRow, ready: Edit[]) {
  const val = (f: FieldKey) => ready.find((e) => e.field === f)?.value
  const status = val('status')
  const loc = val('location')
  const oper = val('operational')
  const fleet = val('in_general_fleet')
  const hold = val('hold_reason')
  return {
    lifecycle_status: status ?? row.lifecycle_status,
    in_general_fleet: fleet === undefined ? row.in_general_fleet : fleet === 'yes',
    hold_reasons:
      hold === undefined ? row.hold_reasons : hold === 'none' ? [] : [hold],
    operational: oper === undefined ? row.operational : oper === 'yes',
    location: loc === undefined ? row.location : loc,
  }
}

/**
 * Change several fields on many vehicles in one pass, and keep the selection.
 *
 * Two things this fixes, both raised by Luyanda on 22 August 2026:
 *
 * 1. The old dialog applied ONE field per pass, so correcting a batch's status
 *    and its location was two trips through the same selection.
 * 2. It cleared the selection on success. That is defensible in the abstract and
 *    wrong in practice: an operator who has just filtered down to fourteen
 *    vehicles and ticked them wants to make the next change to the same
 *    fourteen, not rebuild the selection. The selection now survives, and the
 *    caller refreshes the rows underneath it.
 *
 * It writes through fleet/bulkEditVehicles, which loops update_vehicle_v1 once
 * per vehicle so the per-row capability check and audit entry are preserved. One
 * vehicle failing does not roll back the others, and the failures are reported
 * per vehicle.
 */
export function BulkEditDialog({
  selectedRows, onDone,
}: { selectedRows: FleetRow[]; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [edits, setEdits] = useState<Edit[]>([])
  const [failures, setFailures] = useState<{ reg: string; error: string }[]>([])
  const bulk = useBulkEditVehicles()

  const used = new Set(edits.map((e) => e.field))
  const available = FIELDS.filter((f) => !used.has(f.key))

  function reset() { setEdits([]); setFailures([]) }

  function addEdit() {
    const next = available[0]
    if (!next) return
    setEdits((prev) => [...prev, { field: next.key, value: '' }])
  }

  function setEdit(i: number, patch: Partial<Edit>) {
    setEdits((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)))
    setFailures([])
  }

  function removeEdit(i: number) {
    setEdits((prev) => prev.filter((_, idx) => idx !== i))
  }

  const ready = edits.filter((e) => e.value !== '')

  // A vehicle is in the batch if AT LEAST ONE of the chosen fields would change
  // on it. Vehicles where every chosen field already holds the target value are
  // listed separately rather than silently included, so the count on the button
  // is the number of vehicles that will actually be written.
  const preview = useMemo(() => {
    const willChange: { id: string; reg: string; changes: string[] }[] = []
    const alreadySet: { id: string; reg: string }[] = []
    const blocked: { id: string; reg: string; why: string }[] = []
    if (ready.length === 0) return { willChange, alreadySet, blocked }

    for (const row of selectedRows) {
      const reg = row.registration_number || '—'
      if (ready.some((e) => e.field === 'status') && row.lifecycle_status === 'written_off') {
        blocked.push({ id: row.vehicle_id, reg, why: 'written off' })
        continue
      }
      // A batch cannot take a car off the road. The incident report the database
      // demands is one report per car, filed against that car, and there is no
      // honest way to write it forty at a time. The gate in update_vehicle_v1
      // would refuse each of these anyway; refusing here means the operator sees
      // which cars need the individual route instead of a list of failures after
      // the fact.
      if (isActive(rowActiveInput(row)) && !isActive(pendingActiveInput(row, ready))) {
        blocked.push({ id: row.vehicle_id, reg, why: 'this would take it off the road' })
        continue
      }
      const changes: string[] = []
      for (const e of ready) {
        const cur = currentValue(e.field, row)
        if (cur !== e.value) {
          changes.push(`${SPEC[e.field].label}: ${labelFor(e.field, cur)} to ${labelFor(e.field, e.value)}`)
        }
      }
      if (changes.length === 0) alreadySet.push({ id: row.vehicle_id, reg })
      else willChange.push({ id: row.vehicle_id, reg, changes })
    }
    return { willChange, alreadySet, blocked }
  }, [ready, selectedRows])

  async function apply() {
    if (preview.willChange.length === 0) return
    setFailures([])
    const patch: Record<string, unknown> = {}
    for (const e of ready) patch[SPEC[e.field].patchKey] = patchValue(e.field, e.value)

    try {
      const res = (await bulk.trigger({
        vehicle_ids: preview.willChange.map((w) => w.id),
        patch,
      }).result) as {
        applied: number
        failed: number
        fields: string[]
        rows: { vehicle_id: string; ok: boolean; error: string | null }[]
      }

      if (res.failed === 0) {
        toast.success(
          `${res.applied} vehicle${res.applied === 1 ? '' : 's'} updated, ${res.fields.length} field${res.fields.length === 1 ? '' : 's'} each`,
        )
        setOpen(false)
        reset()
        // The selection is NOT cleared. onDone refreshes the rows underneath it.
        onDone()
        return
      }

      toast.error(`${res.applied} updated, ${res.failed} failed`)
      const regById = new Map(selectedRows.map((r) => [r.vehicle_id, r.registration_number || '—']))
      setFailures(
        res.rows.filter((r) => !r.ok).map((r) => ({
          reg: regById.get(r.vehicle_id) ?? r.vehicle_id,
          error: writeErrorMessage(r.error),
        })),
      )
      // Partial success still changed rows, so refresh even while the dialog stays open.
      onDone()
    } catch (err) {
      toast.error('Could not apply the changes', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (next) reset(); setOpen(next) }}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-7 gap-1.5 px-2 text-xs" disabled={selectedRows.length === 0}>
          <Wand2 className="h-3.5 w-3.5" />
          Edit selected
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit {selectedRows.length} vehicle{selectedRows.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Change as many fields as you need in one pass. The selection stays after the change, so
            you can keep working with the same vehicles.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {edits.length === 0 && (
            <p className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
              No changes yet. Add a field to get started.
            </p>
          )}

          {edits.map((e, i) => {
            const spec = SPEC[e.field]
            return (
              <div key={`${e.field}-${i}`} className="flex flex-wrap items-end gap-2 rounded-md border border-border p-2">
                <div className="min-w-[170px] flex-1 space-y-1">
                  <Label htmlFor={`bf-${i}`} className="text-xs">Field</Label>
                  <NativeSelect id={`bf-${i}`} value={e.field}
                    onChange={(ev) => setEdit(i, { field: ev.target.value as FieldKey, value: '' })}>
                    <option value={e.field}>{spec.label}</option>
                    {available.map((f) => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </NativeSelect>
                </div>
                <div className="min-w-[170px] flex-1 space-y-1">
                  <Label htmlFor={`bv-${i}`} className="text-xs">New value</Label>
                  {spec.kind === 'select' ? (
                    <NativeSelect id={`bv-${i}`} value={e.value}
                      onChange={(ev) => setEdit(i, { value: ev.target.value })}>
                      <option value="">Choose a value</option>
                      {(spec.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </NativeSelect>
                  ) : (
                    <Input id={`bv-${i}`} type={spec.kind === 'date' ? 'date' : 'text'}
                      value={e.value} onChange={(ev) => setEdit(i, { value: ev.target.value })} />
                  )}
                </div>
                <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => removeEdit(i)}
                  aria-label={`Remove ${spec.label}`}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}

          <Button variant="outline" size="sm" className="h-8 gap-1.5 px-2 text-xs"
            onClick={addEdit} disabled={available.length === 0}>
            <Plus className="h-3.5 w-3.5" />
            Add a field
          </Button>
        </div>

        {ready.length > 0 && (
          <div className="space-y-3">
            <PreviewGroup title={`Will change (${preview.willChange.length})`}
              empty="None of the selected vehicles would change." items={preview.willChange}
              render={(r) => (
                <div>
                  <span className="font-mono font-semibold text-foreground">{r.reg}</span>
                  <ul className="ml-4 mt-0.5 space-y-0.5">
                    {r.changes.map((c) => (
                      <li key={c} className="flex items-center gap-1 text-muted-foreground">
                        <ArrowRight className="h-3 w-3 shrink-0" />{c}
                      </li>
                    ))}
                  </ul>
                </div>
              )} />
            <PreviewGroup title={`Already set (${preview.alreadySet.length})`} empty="None."
              items={preview.alreadySet}
              render={(r) => <span className="font-mono font-semibold text-muted-foreground">{r.reg}</span>} />
            {preview.blocked.length > 0 && (
              <PreviewGroup title={`Blocked (${preview.blocked.length})`} empty="None."
                note="Written off is a decision, and taking a car off the road needs its own incident report. Both are corrected one vehicle at a time, from the row on the Fleet list."
                items={preview.blocked}
                render={(r) => (
                  <span className="text-muted-foreground">
                    <span className="font-mono font-semibold">{r.reg}</span> · {r.why}
                  </span>
                )} />
            )}
          </div>
        )}

        {failures.length > 0 && (
          <div className="space-y-1.5 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <div className="flex items-center gap-1.5 font-medium">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {failures.length} vehicle{failures.length === 1 ? '' : 's'} could not be updated
            </div>
            <ul className="space-y-0.5">
              {failures.map((f, i) => (
                <li key={`${f.reg}-${i}`} className="flex flex-wrap gap-x-2">
                  <span className="font-mono font-semibold">{f.reg}</span>
                  <span className="text-destructive/80">{f.error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={bulk.loading}>Close</Button>
          <Button onClick={apply} disabled={preview.willChange.length === 0 || bulk.loading}>
            {bulk.loading && <Spinner className="mr-2 h-4 w-4" />}
            Apply to {preview.willChange.length} vehicle{preview.willChange.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PreviewGroup<T extends { id: string }>({
  title, empty, note, items, render,
}: {
  title: string
  empty: string
  note?: string
  items: T[]
  render: (item: T) => ReactNode
}) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {note && <p className="mb-1.5 text-xs text-muted-foreground">{note}</p>}
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1 text-xs">
          {items.map((item) => <li key={item.id}>{render(item)}</li>)}
        </ul>
      )}
    </div>
  )
}
